import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export type UserRole = 'admin' | 'professional' | 'consumer';

interface UserRoleState {
  role: UserRole;
  loading: boolean;
  isAuthenticated: boolean;
}

/**
 * Hook to get the current user's role.
 * Uses server-side validation via the get_user_role() RPC function.
 * 
 * Roles:
 * - admin: users in user_roles table with admin role
 * - professional: registered healthcare professionals
 * - consumer: all other authenticated users
 */
export function useUserRole(): UserRoleState {
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const [role, setRole] = useState<UserRole>('consumer');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRole = async () => {
      if (!user) {
        setRole('consumer');
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase.rpc('get_user_role');
        
        if (error) {
          console.error('Error fetching user role:', error);
          setRole('consumer');
        } else {
          setRole((data as UserRole) || 'consumer');
        }
      } catch (err) {
        console.error('Failed to fetch user role:', err);
        setRole('consumer');
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      fetchRole();
    }
  }, [user, authLoading]);

  return {
    role,
    loading: authLoading || loading,
    isAuthenticated,
  };
}

/**
 * Get redirect path based on user role.
 * NOTE: Professional no longer has a special redirect since /profissional is public.
 */
export function getRedirectPathForRole(role: UserRole): string {
  switch (role) {
    case 'admin':
      return '/dashboard';
    case 'professional':
    case 'consumer':
    default:
      // All non-admin users go to consumer profile
      // /profissional content is public (no auth needed)
      return '/perfil';
  }
}
